import axios from 'axios';
import API_MAP, { API_DOMAIN, API_DOMAIN_RECORDING, WHITEBOARDS_DOMAIN } from 'constants/api';
import queryString from 'query-string';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from 'utils/cookies';
import { directToAuth } from 'utils/functions';
import { setIsLoaded, setIsLoading } from 'utils/helpers';
// Set up default config for http requests here
// Please have a look at here `https://github.com/axios/axios#request- config` for the full list of configs

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

function safeParse(str) {
    try {
        return JSON.parse(str);
    } catch (e) {
        return str;
    }
}

export const refreshTokenWrapper = async (inputToken, isRedirect = true) => {
    if (isRefreshing) {
        return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
        })
            .then(token => {
                return token;
            })
            .catch(err => {
                return Promise.reject(err);
            });
    }

    isRefreshing = true;

    return new Promise(function (resolve, reject) {
        axios
            .post(API_MAP.AUTH.REFRESH_TOKEN, { refreshToken: getRefreshToken() || inputToken })
            .then((res) => {
                const { token, refreshToken } = res.data?.detail
                setTokens(token, refreshToken)
                processQueue(null, token);
                resolve(token);
            })
            .catch(err => {
                processQueue(err, null);
                localStorage.removeItem('persist:root');
                clearTokens()
                directToAuth(isRedirect)
                reject(err);
            })
            .finally(() => {
                isRefreshing = false;
            });
    });
}

const responseInterceptors = async (error, originCaller, isRedirect = true) => {
    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {

        try {
            const token = await refreshTokenWrapper(getRefreshToken(), isRedirect);
            originalRequest._retry = true;
            originCaller.defaults.headers.common['token'] = token;
            originalRequest.headers['token'] = token;
            if (originalRequest?.data) {
                originalRequest.data = safeParse(originalRequest?.data)
            }
            return originCaller(originalRequest);
        } catch (e) {
            return Promise.reject(e);
        }

    } else {
        // let url = window.location.href;
        if (error.response) {
            // eslint-disable-next-line default-case
            switch (error.response.status) {
                case 401:
                    // const href = url ? `?domain=${url}` : '';
                    localStorage.removeItem('persist:root');
                    clearTokens()
                    if (!window.location.href.includes('/login') && isRedirect) {
                        window.location.href = `/login`;
                    }
                    break;
            }
        }
    }
    return Promise.reject(error);
}

const axiosClient = axios.create({
    baseURL: API_DOMAIN,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

axiosClient.interceptors.request.use(async (config) => {
    const token = getAccessToken()
    setIsLoading()
    config.headers = { token: token, Authorization: `Bearer ${token}` };
    return config;
});

axiosClient.interceptors.response.use(
    (response) => {
        setIsLoaded()
        return response.data;
    },
    async (error) => await responseInterceptors(error, axiosClient),
);

const axiosFullResponse = axios.create({
    baseURL: API_DOMAIN,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

axiosFullResponse.interceptors.request.use(async (config) => {
    const token = getAccessToken()
    config.headers = { token: token, Authorization: `Bearer ${token}` };
    return config;
});

axiosFullResponse.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => await responseInterceptors(error, axiosFullResponse),
);

const whiteboardClient = axios.create({
    baseURL: WHITEBOARDS_DOMAIN,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

whiteboardClient.interceptors.request.use(async (config) => {
    const token = getAccessToken()
    config.headers = { token: token };
    return config;
});

whiteboardClient.interceptors.response.use(
    (response) => {
        return response.data;
    },
    async (error) => await responseInterceptors(error, whiteboardClient),
);


const axiosClientNoRedirect = axios.create({
    baseURL: API_DOMAIN,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

axiosClientNoRedirect.interceptors.request.use(async (config) => {
    const token = getAccessToken()
    setIsLoading()
    config.headers = { token: token };
    return config;
});

axiosClientNoRedirect.interceptors.response.use(
    (response) => {
        setIsLoaded()
        return response.data;
    }
    // ,
    // async (error) => await responseInterceptors(error, axiosClientNoRedirect, false),
);

const axiosRecordingResponse = axios.create({
    baseURL: API_DOMAIN_RECORDING,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

axiosRecordingResponse.interceptors.request.use(async (config) => {
    const token = getAccessToken()
    config.headers = { Authorization: 'Bearer ' + token };
    return config;
});

axiosRecordingResponse.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => await responseInterceptors(error, axiosRecordingResponse),
);

export default axiosClient;
export { axiosClientNoRedirect, axiosFullResponse, axiosRecordingResponse, whiteboardClient };

